<template>
  <Teleport to="body">
    <div
      class="document-preview-modal fixed inset-0 z-[100] flex flex-col"
      aria-labelledby="document-preview-title"
      role="dialog"
      aria-modal="true"
    >
      <div
        @click="dismiss"
        class="fixed inset-0 bg-gray-500/75 transition-opacity"
        aria-hidden="true"
      ></div>

      <div class="relative z-10 flex flex-col w-full h-full p-0 sm:p-3 md:p-4">
        <div
          class="flex flex-col flex-1 min-h-0 w-full max-w-[100vw] bg-white shadow-xl overflow-hidden sm:rounded-lg"
          @click.stop
        >
          <header class="shrink-0 border-b border-paleBlue bg-white">
            <div class="flex items-center justify-between gap-4 px-4 py-3 sm:px-6">
              <div class="min-w-0 text-left">
                <p class="text-sm font-normal text-mediumBlue truncate">Preview</p>
                <h2
                  id="document-preview-title"
                  class="text-lg sm:text-2xl leading-6 text-darkBlue truncate"
                >
                  {{ displayTitle }}
                </h2>
              </div>
              <button
                ref="closeButton"
                type="button"
                class="text-gtBrightBlue shrink-0 cursor-pointer focus:outline-none focus:ring-2 focus:ring-gtBrightBlue focus:ring-offset-2 rounded-full"
                aria-label="Close document preview"
                @click="dismiss"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="currentColor" class="bi bi-x-circle" viewBox="0 0 16 16" aria-hidden="true">
                  <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/>
                  <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z"/>
                </svg>
              </button>
            </div>

            <div
              v-if="hasMultipleDocuments"
              class="flex gap-2 px-4 py-3 sm:px-6 overflow-x-auto"
              role="tablist"
              aria-label="Document selection"
            >
              <button
                v-for="(doc, index) in documents"
                :key="index"
                type="button"
                role="tab"
                :aria-selected="activeDocumentIndex === index"
                :class="[
                  'shrink-0 px-4 py-1.5 rounded-3xl text-sm font-medium ease-out duration-100 focus:outline-none focus:ring-2 focus:ring-gtBrightBlue focus:ring-offset-2',
                  activeDocumentIndex === index
                    ? 'bg-buttonBlue text-trueWhite'
                    : 'border-2 border-buttonBlue text-buttonBlue hover:bg-darkBlue hover:border-darkBlue hover:text-trueWhite',
                ]"
                @click="selectDocument(index)"
              >
                {{ doc.title }}
              </button>
            </div>
          </header>

          <div class="flex-1 min-h-0 overflow-hidden bg-gtGray300">
            <iframe
              v-if="safeDocUrl"
              :key="safeDocUrl"
              :src="safeDocUrl"
              class="block w-full h-full border-0"
              title="Document preview"
              aria-label="pdf preview"
            ></iframe>
            <div
              v-else
              class="flex items-center justify-center h-full px-6 text-center text-darkBlue"
              role="alert"
            >
              This document cannot be previewed safely.
            </div>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script>
import { isSafeDocumentPreviewUrl } from '@/utilities/safeDocumentUrl';

export default {
  name: 'DocumentPreviewModal',
  props: {
    documents: {
      type: Array,
      required: true,
      validator: (value) =>
        value.length > 0 &&
        value.every(
          (doc) =>
            doc &&
            typeof doc.title === 'string' &&
            typeof doc.url === 'string',
        ),
    },
  },
  emits: ['dismiss'],
  data() {
    return {
      previousOverflow: '',
      activeDocumentIndex: 0,
    };
  },
  computed: {
    hasMultipleDocuments() {
      return this.documents.length > 1;
    },
    activeDocument() {
      return this.documents[this.activeDocumentIndex] || this.documents[0];
    },
    displayTitle() {
      return this.activeDocument?.title || 'Document Preview';
    },
    safeDocUrl() {
      const url = this.activeDocument?.url;
      return isSafeDocumentPreviewUrl(url) ? url.trim() : '';
    },
  },
  mounted() {
    this.previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', this.onKeydown);
  },
  beforeUnmount() {
    document.body.style.overflow = this.previousOverflow || '';
    document.removeEventListener('keydown', this.onKeydown);
  },
  methods: {
    selectDocument(index) {
      this.activeDocumentIndex = index;
    },
    dismiss() {
      this.$emit('dismiss');
    },
    onKeydown(event) {
      if (event.key === 'Escape') {
        this.dismiss();
      }
    },
  },
};
</script>

<style></style>
